import { useCallback, useEffect, useRef, useState } from "react";
import { useConfirm } from "context/ConfirmContext";
import { getBearerToken } from "services/tenantUserAdminService";
import PropTypes from "prop-types";
import {
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArticleIcon from "@mui/icons-material/Article";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Dialog, DialogContent, DialogTitle, IconButton as MuiIconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import {
  deleteClientDocument,
  getAttachmentUrl,
  listClientInbox,
  listClientDocuments,
  uploadClientDocument,
} from "services/tenantUserAdminService";
import { TC, FS, FW } from "../../tokens/text";

const ACCEPTED_FORMATS = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif,.webp,.bmp";
const API_BASE_URL = process.env.REACT_APP_ACCOUNTING_API_BASE_URL || "http://localhost:4001/api/v1";
const CATEGORIES = [
  "Accounts",
  "Tax",
  "Company Secretarial",
  "Correspondence",
  "Other",
];

function normaliseDocuments(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.documents)) return payload.documents;
  return [];
}

function normaliseEmailAttachmentDocuments(payload) {
  const emails = Array.isArray(payload?.emails) ? payload.emails : [];

  return emails.flatMap((email) => {
    const attachments = Array.isArray(email?.attachments) ? email.attachments : [];

    return attachments.map((attachment, index) => ({
      id: `email-attachment:${email.id}:${index}`,
      source: "emailAttachment",
      emailId: email.id,
      attachmentIndex: index,
      name: attachment?.name || `Attachment ${index + 1}`,
      filename: attachment?.name || `Attachment ${index + 1}`,
      mimeType: attachment?.contentType || "application/octet-stream",
      contentType: attachment?.contentType || "application/octet-stream",
      size: Number(attachment?.size || 0),
      category: "Correspondence",
      uploadedAt: email?.date || email?.receivedAt || email?.createdAt || null,
      createdAt: email?.date || email?.receivedAt || email?.createdAt || null,
      storagePath: attachment?.storagePath || "",
      sourceEmailSubject: email?.subject || "(No subject)",
      sourceEmailFrom: email?.from || email?.fromEmail || "",
    }));
  });
}

function getDocumentId(doc) {
  return doc?.id || doc?._id || doc?.documentId || doc?.docId;
}

function getDocumentName(doc) {
  return doc?.filename || doc?.fileName || doc?.name || doc?.originalName || "Untitled document";
}

function getDocumentCategory(doc) {
  return doc?.category || "Other";
}

function getDocumentDate(doc) {
  return doc?.uploadedAt || doc?.createdAt || doc?.updatedAt || null;
}

function getDocumentSize(doc) {
  return Number(doc?.size || doc?.fileSize || doc?.bytes || 0);
}

function getDownloadUrl(doc, tenantId, clientId) {
  if (doc?.source === "emailAttachment") return "";
  if (!doc?.id || !tenantId || !clientId) return "";
  return `${API_BASE_URL}/tenant/${encodeURIComponent(tenantId)}/client-profiles/${encodeURIComponent(clientId)}/documents/${encodeURIComponent(doc.id)}/download`;
}

function isPreviewable(doc) {
  const name = getDocumentName(doc).toLowerCase();
  const type = (doc?.mimeType || "").toLowerCase();
  return type.includes("pdf") || name.endsWith(".pdf") ||
         type.includes("image") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
}

function formatDate(value) {
  if (!value) return "Unknown upload date";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatFileSize(value) {
  if (!value) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(doc) {
  const name = getDocumentName(doc).toLowerCase();
  const type = (doc?.mimeType || doc?.contentType || "").toLowerCase();

  if (type.includes("pdf") || name.endsWith(".pdf")) {
    return <PictureAsPdfIcon sx={{ color: "#d32f2f", fontSize: "1.1rem" }} />;
  }
  if (type.includes("image") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name)) {
    return <ImageIcon sx={{ color: "#7c4dff", fontSize: "1.1rem" }} />;
  }
  if (type.includes("sheet") || /\.(xls|xlsx|csv)$/i.test(name)) {
    return <TableChartIcon sx={{ color: "#2e7d32", fontSize: "1.1rem" }} />;
  }
  if (type.includes("word") || /\.(doc|docx)$/i.test(name)) {
    return <ArticleIcon sx={{ color: "#1565c0", fontSize: "1.1rem" }} />;
  }
  return <DescriptionIcon sx={{ color: TC.link, fontSize: "1.1rem" }} />;
}

export default function DocumentsTab({ tenantId, clientId }) {
  const confirm  = useConfirm();
  const inputRef = useRef(null);
  const [category, setCategory] = useState("Other");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preview, setPreview] = useState(null); // { url, name, mimeType }

  const loadDocuments = useCallback(async () => {
    if (!tenantId || !clientId) {
      setDocuments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [documentsPayload, inboxPayload] = await Promise.all([
        listClientDocuments(tenantId, clientId),
        listClientInbox(tenantId, clientId, 200),
      ]);
      const mergedDocuments = [
        ...normaliseDocuments(documentsPayload),
        ...normaliseEmailAttachmentDocuments(inboxPayload),
      ].sort((a, b) => {
        const aTime = getDocumentDate(a) ? new Date(getDocumentDate(a)).getTime() : 0;
        const bTime = getDocumentDate(b) ? new Date(getDocumentDate(b)).getTime() : 0;
        return bTime - aTime;
      });
      setDocuments(mergedDocuments);
    } catch (err) {
      setError(err?.message || "Failed to load documents.");
    } finally {
      setLoading(false);
    }
  }, [tenantId, clientId]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleUpload = async (file) => {
    if (!file || uploading) return;
    setUploading(true);
    setError("");
    setSuccess("");
    try {
      await uploadClientDocument(tenantId, clientId, file, category);
      setSuccess(`${file.name} uploaded successfully.`);
      await loadDocuments();
    } catch (err) {
      setError(err?.message || "Upload failed.");
    } finally {
      // Reset input so the same file can be re-uploaded
      if (inputRef.current) inputRef.current.value = "";
      setUploading(false);
      setDragActive(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!uploading) handleUpload(e.dataTransfer.files?.[0]);
  };

  const handleDelete = async (doc) => {
    if (doc?.source === "emailAttachment") {
      setError("Email attachments are managed from the Inbox tab.");
      return;
    }
    const docId = getDocumentId(doc);
    if (!docId) { setError("Cannot delete — document has no id."); return; }
    const ok = await confirm({
      title: "Delete document?",
      message: `"${getDocumentName(doc)}" will be permanently removed. This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    setError(""); setSuccess("");
    try {
      await deleteClientDocument(tenantId, clientId, docId);
      setSuccess(`${getDocumentName(doc)} deleted.`);
      await loadDocuments();
    } catch (err) {
      setError(err?.message || "Delete failed.");
    }
  };

  const handleDownload = (doc) => {
    if (doc?.source === "emailAttachment") {
      if (!doc?.storagePath) {
        setError("This email attachment has not been uploaded to storage yet.");
        return;
      }
      getAttachmentUrl(tenantId, clientId, doc.emailId, doc.attachmentIndex, "download")
        .then((response) => {
          if (!response?.url) throw new Error("No download link available for this attachment.");
          window.open(response.url, "_blank", "noopener,noreferrer");
        })
        .catch((err) => {
          setError(err?.message || "Failed to download attachment.");
        });
      return;
    }
    const url = getDownloadUrl(doc, tenantId, clientId);
    if (!url) { setError("No download link available for this document."); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePreview = async (doc) => {
    if (doc?.source === "emailAttachment") {
      if (!doc?.storagePath) {
        setError("This email attachment has not been uploaded to storage yet.");
        return;
      }
      try {
        const response = await getAttachmentUrl(tenantId, clientId, doc.emailId, doc.attachmentIndex, "preview");
        if (!response?.url) throw new Error("No preview link available for this attachment.");
        setPreview({ url: response.url, name: getDocumentName(doc), mimeType: doc?.mimeType || doc?.contentType || "" });
      } catch (err) {
        setError(err?.message || "Could not load preview.");
      }
      return;
    }
    const url = getDownloadUrl(doc, tenantId, clientId);
    if (!url) { setError("Cannot preview this document."); return; }
    try {
      const token = await getBearerToken();
      const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const blob    = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreview({ url: blobUrl, name: getDocumentName(doc), mimeType: blob.type || doc?.mimeType || "" });
    } catch (err) {
      setError(err.message || "Could not load preview.");
    }
  };

  return (
    <VuiBox>
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={2.5}>
        <VuiBox>
          <VuiTypography variant="button" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.base }}>
            Client Documents
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mt: 0.4 }}>
            Store supporting files for this client profile in one place.
          </VuiTypography>
        </VuiBox>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <Select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            displayEmpty
            sx={{
              background: "#f8fbff",
              borderRadius: "8px",
              color: TC.primary,
              fontSize: FS.sm,
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d8e6f7" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#b6cbe4" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TC.link },
            }}
          >
            {CATEGORIES.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </VuiBox>

      {(error || success) && (
        <VuiBox
          mb={2}
          px={1.5}
          py={1.1}
          sx={{
            borderRadius: "8px",
            background: error ? "rgba(244,67,54,0.08)" : "rgba(76,175,80,0.08)",
            border: error ? "1px solid rgba(244,67,54,0.25)" : "1px solid rgba(76,175,80,0.25)",
          }}
        >
          <VuiTypography sx={{ color: error ? TC.error : TC.success, fontSize: FS.sm }}>
            {error || success}
          </VuiTypography>
        </VuiBox>
      )}

      {/* Drop zone — input nested inside label, invisible overlay, works in all browsers */}
      {/* Hidden file input — triggered by button below */}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FORMATS}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Drop zone */}
      <VuiBox
        onDragEnter={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
        onDragOver={(e)  => { e.preventDefault(); if (!uploading) setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false); }}
        onDrop={handleDrop}
        mb={3}
        sx={{
          position: "relative",
          border: `2px dashed ${dragActive ? "#0075ff" : "#d8e6f7"}`,
          background: dragActive ? "rgba(0,117,255,0.08)" : "#f4f8ff",
          borderRadius: "14px", padding: "28px 24px", textAlign: "center",
          transition: "all 0.2s ease",
        }}
      >
        <VuiBox mx="auto" mb={1.25}
          sx={{ width: 52, height: 52, borderRadius: "14px",
            background: "rgba(0,117,255,0.12)", border: "1px solid rgba(0,117,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          {uploading
            ? <CircularProgress size={24} sx={{ color: "#0075ff" }} />
            : <CloudUploadRoundedIcon sx={{ color: "#0075ff", fontSize: "1.4rem" }} />}
        </VuiBox>
        <VuiTypography variant="button" sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.md, display: "block", mb: 0.6 }}>
          {uploading ? "Uploading..." : "Drag & drop a file here, or"}
        </VuiTypography>
        <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm, display: "block", mb: 1.5 }}>
          PDF, Word, Excel and image files supported
        </VuiTypography>
        {/* Standard button → inputRef.current.click() is always a trusted gesture */}
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current && inputRef.current.click()}
          style={{
            background: "#0075ff", color: "#fff", border: "none",
            borderRadius: 8, padding: "7px 20px", fontSize: 13,
            fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
          }}
        >
          Choose File
        </button>
        {category && (
          <VuiBox mt={1.25}>
            <Chip label={`Category: ${category}`} size="small"
              sx={{ height: 22, fontSize: FS.xs, fontWeight: FW.semibold,
                bgcolor: "rgba(0,117,255,0.12)", color: "#0075ff", border: "1px solid rgba(0,117,255,0.24)" }} />
          </VuiBox>
        )}
      </VuiBox>
      {loading ? (
        <VuiBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} sx={{ color: TC.link }} />
        </VuiBox>
      ) : documents.length === 0 ? (
        <VuiBox
          px={2}
          py={3}
          sx={{
            borderRadius: "12px",
            background: "#f8fbff",
            border: "1px solid #d8e6f7",
            textAlign: "center",
          }}
        >
          <DescriptionIcon sx={{ color: "#9fb4cf", fontSize: "1.8rem", mb: 0.5 }} />
          <VuiTypography variant="button" sx={{ color: TC.primary, fontSize: FS.md, display: "block", mb: 0.5 }}>
            No documents uploaded
          </VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary, fontSize: FS.sm }}>
            Add accounts, tax, secretarial or correspondence files for this client.
          </VuiTypography>
        </VuiBox>
      ) : (
        <VuiBox display="flex" flexDirection="column" gap={1}>
          {documents.map((doc) => {
            const docId = getDocumentId(doc) || getDocumentName(doc);
            const canPreview = isPreviewable(doc);
            return (
              <VuiBox
                key={docId}
                px={2} py={1.25}
                sx={{
                  background: "#fff", border: "1px solid #d8e6f7",
                  borderRadius: "12px", display: "flex",
                  alignItems: "center", gap: 1.5,
                }}
              >
                {/* Icon */}
                <VuiBox sx={{ width: 38, height: 38, borderRadius: "9px", background: "#f4f8ff",
                  border: "1px solid #d8e6f7", display: "flex", alignItems: "center",
                  justifyContent: "center", flexShrink: 0 }}>
                  {getFileIcon(doc)}
                </VuiBox>

                {/* Name + date */}
                <VuiBox flex={1} minWidth={0}>
                  <VuiTypography sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.bold,
                    display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {getDocumentName(doc)}
                  </VuiTypography>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mt: 0.3 }}>
                    {formatDate(getDocumentDate(doc))} &nbsp;·&nbsp; {formatFileSize(getDocumentSize(doc))}
                    &nbsp;·&nbsp; {getDocumentCategory(doc)}
                  </VuiTypography>
                  {doc?.source === "emailAttachment" && (
                    <VuiTypography sx={{ color: TC.secondary, fontSize: FS.xs, display: "block", mt: 0.3 }}>
                      From email: {doc.sourceEmailSubject}
                      {doc.sourceEmailFrom ? ` · ${doc.sourceEmailFrom}` : ""}
                    </VuiTypography>
                  )}
                </VuiBox>

                {/* Actions */}
                <VuiBox display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                  {doc?.source === "emailAttachment" && (
                    <Chip
                      label="Email attachment"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: FS.xs,
                        fontWeight: FW.semibold,
                        bgcolor: "rgba(0,117,255,0.12)",
                        color: "#0075ff",
                        border: "1px solid rgba(0,117,255,0.24)",
                      }}
                    />
                  )}
                  <Tooltip title={canPreview ? "Preview" : "Download"}>
                    <IconButton
                      size="small"
                      onClick={() => canPreview ? handlePreview(doc) : handleDownload(doc)}
                      aria-label={`${canPreview ? "Preview" : "Download"} document ${getDocumentName(doc)}`}
                      sx={{ color: "#0075ff", borderRadius: "8px", border: "1px solid rgba(0,117,255,0.25)",
                        px: 1, "&:hover": { background: "rgba(0,117,255,0.08)" } }}>
                      {canPreview
                        ? <VisibilityIcon sx={{ fontSize: "0.95rem" }} aria-hidden="true" />
                        : <DownloadIcon sx={{ fontSize: "0.95rem" }} aria-hidden="true" />}
                    </IconButton>
                  </Tooltip>
                  {doc?.source !== "emailAttachment" && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(doc)}
                        aria-label={`Delete document ${getDocumentName(doc)}`}
                        sx={{ color: "#d32f2f", borderRadius: "8px", border: "1px solid rgba(211,47,47,0.25)",
                          px: 1, "&:hover": { background: "rgba(211,47,47,0.08)" } }}>
                        <DeleteIcon sx={{ fontSize: "0.95rem" }} aria-hidden="true" />
                      </IconButton>
                    </Tooltip>
                  )}
                </VuiBox>
              </VuiBox>
            );
          })}
        </VuiBox>
      )}

      {/* Preview modal */}
      <Dialog
        open={!!preview}
        onClose={() => {
          if (preview?.url?.startsWith("blob:")) URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
        aria-labelledby="document-preview-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="document-preview-dialog-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5, px: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {preview?.name}
          </span>
          <MuiIconButton
            size="small"
            onClick={() => {
              if (preview?.url?.startsWith("blob:")) URL.revokeObjectURL(preview.url);
              setPreview(null);
            }}
            aria-label="Close document preview"
          >
            <CloseIcon fontSize="small" />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 400, display: "flex", alignItems: "center", justifyContent: "center", background: "#111" }}>
          {preview && (preview.mimeType.includes("image") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(preview.name)
            ? <img src={preview.url} alt={preview.name} style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
            : <iframe src={preview.url} title={preview.name} style={{ width: "100%", height: "75vh", border: "none" }} />
          )}
        </DialogContent>
      </Dialog>
    </VuiBox>
  );
}

DocumentsTab.propTypes = {
  tenantId: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
};

